import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../utils/authService';

// Get admin path from environment variables or use the default
const ADMIN_ACCESS_PATH = process.env.REACT_APP_ADMIN_PATH || 'secure-admin-route';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);
  
  // Validate authentication on mount only
  useEffect(() => {
    // Don't repeatedly attempt validation
    if (validationAttempted) return;
    
    const validateAuth = async () => {
      setIsLoading(true);
      
      try {
        // Check if we have a valid access token first
        if (!authService.isAuthenticated()) {
          setIsAuthenticated(false);
          setIsLoading(false);
          setValidationAttempted(true);
          return;
        }
        
        // Validate the session with the backend
        const result = await authService.validateSession();
        setIsAuthenticated(result.success);
      } catch (error) {
        console.error('Authentication validation error:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
        setValidationAttempted(true);
      }
    };
    
    validateAuth();
  }, [validationAttempted]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-dark">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-accent/20 h-12 w-12"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-accent/20 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-accent/20 rounded"></div>
              <div className="h-4 bg-accent/20 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Only redirect to pages that are NOT already the login page to avoid loops
    if (!location.pathname.includes(`/${ADMIN_ACCESS_PATH}/login`)) {
      return <Navigate to={`/${ADMIN_ACCESS_PATH}/login`} state={{ from: location }} replace />;
    }
  }
  
  // Render the protected content
  return <>{children}</>;
};

export default ProtectedRoute; 